import axios from "axios";

// API Client
import CarbonClient from "../CarbonClient";

/**
 * Agent Skills (RTLCA-5600). The skill library behind the AI Agent Skills screen and the chat
 * input's skill picker.
 *
 * These endpoints exist because the per-entity authorization rules live in the backend: a shared
 * skill may only be deleted — or taken back into private ownership — by the user who created it
 * (RTLCA-4989). The screen used to read and write `agentSkillsTenantUser` straight over Hasura,
 * which meant the rule simply did not apply to it. Everything now goes through here so there is
 * one enforcer.
 *
 * Two behaviours differ from the Hasura path this replaced, both deliberate:
 * - DELETE is a **soft** delete (sets `deletedAt`) and soft-deleted skills are absent from every
 *   read. The old `delete_by_pk` hard-deleted, and the old list query had no `deletedAt` filter,
 *   so a skill deleted through the API stayed visible in the UI. That divergence is gone.
 * - The list returns **summaries with no `body`**. Fetch {@link getAgentSkill} for the body when a
 *   skill is actually opened.
 */

/** Ownership level of a skill. The server derives the ownership columns from it; clients never set them. */
export type AgentSkillScope = "curated" | "tenant" | "user" | "project";

/** A row of the list response — everything except `body`. */
export interface AgentSkillSummary {
  id: string;
  scope: AgentSkillScope;
  name: string;
  description: string;
  useWhen: string | null;
  sourceType: string;
  mcpPromptName: string | null;
  mcpServerName: string | null;
  tenantId: string | null;
  userId: string | null;
  projectId: string | null;
  /**
   * The user who created the skill, whatever its scope — stamped once and never rewritten, which is
   * what a later delete is checked against. `null` on skills created before the column existed;
   * those stay deletable by anyone in the tenant, by design.
   */
  creatorId: string | null;
  isCurated: boolean;
  createdAt: string;
  updatedAt: string | null;
}

/** A single skill, including its instructions. */
export interface AgentSkill extends AgentSkillSummary {
  body: string | null;
}

/**
 * The POST and PUT body. They share one shape on purpose, and PUT is a **full replace** rather than
 * a patch — every field goes on every save.
 */
export interface AgentSkillSaveBody {
  name: string;
  description: string;
  useWhen: string | null;
  body: string | null;
  sourceType: string;
  scope: AgentSkillScope;
  /** Required when `scope` is `"project"`, ignored otherwise. */
  projectId?: string;
}

/** Which ownership buckets a list request asks for. */
export interface AgentSkillListFilter {
  includeCurated: boolean;
  includeTenant: boolean;
  includeUser: boolean;
  includeProject: boolean;
}

/**
 * The buckets the Skills screen and the chat picker show: RTLCA's curated skills, the tenant's
 * shared skills, and the caller's own private ones.
 *
 * Curated skills are platform-authored and visible to every tenant. They are part of the library a
 * user picks from, so both surfaces list them and the chat picker offers them (RTLCA-5655); only
 * a platform admin may write to one, which is a per-skill rule the screen reads off `scope` (see
 * `views/Agent/services/skillScope.ts`), not something the list filter should decide.
 *
 * Project-scoped skills stay out: they belong to one project's context, not to the workspace-wide
 * library these two surfaces show.
 */
export const CURATED_TENANT_AND_USER_SKILLS: AgentSkillListFilter = {
  includeCurated: true,
  includeTenant: true,
  includeUser: true,
  includeProject: false,
};

interface ListAgentSkillsResponse {
  skills: AgentSkillSummary[];
}

/** Lists skill summaries (no `body`), newest first. */
export const getAgentSkills = async (
  filter: AgentSkillListFilter = CURATED_TENANT_AND_USER_SKILLS
): Promise<AgentSkillSummary[]> => {
  const response = await CarbonClient.get<ListAgentSkillsResponse>("api/agent-skills", { params: filter });
  if (response.status !== 200) {
    throw new Error(`Failed to load skills: ${response.status}`);
  }
  return response.data?.skills ?? [];
};

/** Returns one skill including its `body`. 404s when it is deleted, another tenant's, or a colleague's private skill. */
export const getAgentSkill = async (id: string): Promise<AgentSkill> => {
  const response = await CarbonClient.get<AgentSkill>(`api/agent-skills/${id}`);
  if (response.status !== 200) {
    throw new Error(`Failed to load skill: ${response.status}`);
  }
  return response.data;
};

/**
 * Creates a skill and returns it as saved. Read the effective `scope` off the result rather than
 * assuming the one sent was honoured — a non-admin asking for `"curated"` is silently downgraded to
 * `"tenant"`.
 */
export const insertAgentSkill = async (body: AgentSkillSaveBody): Promise<AgentSkill> => {
  const response = await CarbonClient.post<AgentSkill>("api/agent-skills", body);
  if (response.status !== 200 && response.status !== 201) {
    throw new Error(`Failed to create skill: ${response.status}`);
  }
  return response.data;
};

/**
 * Replaces a skill and returns it as saved. Same "trust the response, not the request" rule as
 * {@link insertAgentSkill}.
 *
 * Throws on 403 when the request would move a shared skill the caller did not create into private
 * ownership (`scope: "user"`), and on 403 for any edit of a curated skill by a non-platform-admin.
 * Content edits of a shared skill are open to the whole tenant.
 */
export const updateAgentSkill = async (id: string, body: AgentSkillSaveBody): Promise<AgentSkill> => {
  const response = await CarbonClient.put<AgentSkill>(`api/agent-skills/${id}`, body);
  if (response.status !== 200) {
    throw new Error(`Failed to update skill: ${response.status}`);
  }
  return response.data;
};

/**
 * Soft-deletes a skill (204, no body). Throws on 403 when a shared skill's creator is someone else,
 * or when a non-platform-admin targets a curated skill.
 */
export const deleteAgentSkill = async (id: string): Promise<void> => {
  const response = await CarbonClient.delete(`api/agent-skills/${id}`);
  if (response.status !== 204 && response.status !== 200) {
    throw new Error(`Failed to delete skill: ${response.status}`);
  }
};

/**
 * The message to show the user for a failed skill request.
 *
 * Every 4xx from this controller carries `{ detail, error }`, and the `detail` is written to be read
 * by a person — "A shared skill can only be deleted by the user who created it." tells them
 * something the fallback cannot. Surfaced verbatim when present, for 403 and 404 alike.
 */
export const agentSkillErrorDetail = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error) && typeof error.response?.data?.detail === "string") {
    return error.response.data.detail;
  }
  return fallback;
};
